<script lang="ts">
    import {
        UPPER_ARCH,
        LOWER_ARCH,
        UPPER_ARCH_DECID,
        LOWER_ARCH_DECID,
        ODONTO_PATHS,
        surfaceFor,
        createDefaultOdontogramaValue,
        hasDeciduousValue,
        cycleStageValue,
        toggleToothSelected,
        toothDisplayColor,
        teethWithNotes,
        type ToothCell,
        type ToothSurface,
        type OdontogramaValue,
        type OdontoStage,
    } from './odontograma-data'
    import OdontogramaInputs from './OdontogramaInputs.svelte'

    // Ciclo de cores por clique: indefinido (branco, sem legenda) -> stages[0] -> stages[1] ->
    // ... -> stages[last] -> indefinido de novo.
    export let stages: OdontoStage[] = []

    // Estado persistido — bindável (`bind:value`) pelo componente que usa o odontograma, pra
    // poder salvar/restaurar do banco. Chave = fdi (dente) ou `${fdi}:${M|D|V|L|P|O}` (região da
    // face); valor = `label` do estágio atual (não o índice — ver odontograma-data.ts). Ausente =
    // indefinido.
    export let value: OdontogramaValue = createDefaultOdontogramaValue()
    export let readOnly = false
    // Impressão/link público: some com o seletor Permanentes/Decíduos — permanentes sempre
    // aparece, decíduos só se houver algum valor marcado (ver showDeciduos abaixo).
    export let plain = false

    // Único transform de dentes.svg (escala 0.1 + flip vertical) — os viewBox em
    // odontograma-paths.generated.ts já foram calculados considerando este mesmo transform.
    const RAW_TRANSFORM = 'matrix(0.1, 0, 0, -0.1, 0, 1408)'

    let denticao: 'permanentes' | 'deciduos' = 'permanentes'
    $: showPermanentes = plain ? true : denticao === 'permanentes'
    $: showDeciduos = plain ? hasDeciduousValue(value) : denticao === 'deciduos'

    function cycleStage(key: string) {
        if (readOnly) return
        value = cycleStageValue(value, stages, key)
    }

    // O dente inteiro não cicla mais estágio próprio — só liga/desliga a seleção manual (ver
    // toggleToothSelected em odontograma-data.ts). Se alguma face já estiver marcada, o dente já
    // aparece selecionado independente desse marcador (ver toothDisplayColor).
    function onToothClick(fdi: string) {
        if (readOnly) return
        console.log('[odontograma] clique', { fdi, parte: 'dente' })
        value = toggleToothSelected(value, fdi)
    }

    // Cada região da face (M/D/V/L/P/O — nomenclatura odontológica padrão, ver surfaceFor em
    // odontograma-data.ts) é clicável de forma independente — as regiões vêm recortadas do
    // próprio desenho original (ver odontograma-paths.generated.ts / split-subpaths.mjs), não são
    // uma geometria inventada por cima.
    function onFacePartClick(fdi: string, surface: ToothSurface) {
        console.log('[odontograma] clique', { fdi, parte: 'face', regiao: surface })
        cycleStage(`${fdi}:${surface}`)
    }

    function onFacePartKeydown(event: KeyboardEvent, fdi: string, surface: ToothSurface) {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            onFacePartClick(fdi, surface)
        }
    }
</script>

<div class="odonto">
    {#if !readOnly}
        <!-- Versão não gráfica (busca + tags + checkboxes de face) do MESMO `value` acima — as
             duas são só visões diferentes da mesma fonte da verdade, então marcar num lado reflete
             no outro automaticamente, sem plumbing extra. Não faz sentido na impressão (sempre
             readOnly) nem em qualquer outro contexto somente leitura. -->
        <OdontogramaInputs {stages} bind:value />
    {/if}

    {#if !plain}
        <!-- Só diz respeito ao odontograma GRÁFICO abaixo (a lista de dentes acima não tem abas,
             busca entre os 62 dentes de uma vez) — por isso fica depois do OdontogramaInputs, não
             antes. -->
        <div class="odonto-header">
            <div class="odonto-toolbar">
                <div class="seg">
                    <button type="button" class:on={denticao === 'permanentes'} on:click={() => (denticao = 'permanentes')}> Permanentes </button>
                    <button type="button" class:on={denticao === 'deciduos'} on:click={() => (denticao = 'deciduos')}> Decíduos </button>
                </div>
            </div>
        </div>
    {/if}

    {#if showPermanentes}
        {#if plain}<h4 class="denticao-title">Dentes Permanentes</h4>{/if}
        <div class="odonto-body">
            <div class="tooth-band tooth-band-upper">
                <div class="gum-band"></div>
                <div class="arch-row">
                    {#each UPPER_ARCH as cell, i (cell.fdi)}
                        {#if i === 8}<div class="mid-gap"></div>{/if}
                        {@const toothColor = toothDisplayColor(value, stages, cell.fdi)}
                        <button type="button" class="tooth-cell" class:mirrored={cell.mirrored} disabled={readOnly} on:click={() => onToothClick(cell.fdi)}>
                            <svg viewBox={ODONTO_PATHS[cell.ref.tooth].viewBox} aria-label="Dente {cell.fdi}">
                                <!-- Conteúdo (cor do estágio) por baixo; a arte de linha (sempre escura) por
                                     cima — ver comentário em odontograma-paths.generated.ts sobre por que a
                                     arte de linha é só fill (sem stroke): stroke duplicava o contorno. -->
                                <path class="tooth-content" d={ODONTO_PATHS[cell.ref.tooth].background} transform={RAW_TRANSFORM} style:fill={toothColor} />
                                <path class="line-art" d={ODONTO_PATHS[cell.ref.tooth].d} transform={RAW_TRANSFORM} />
                                {#if cell.ref.sliver != null}
                                    <path class="line-art" d={ODONTO_PATHS[cell.ref.sliver].d} transform={RAW_TRANSFORM} />
                                {/if}
                            </svg>
                        </button>
                    {/each}
                </div>
            </div>

            <div class="arch-row">
                {#each UPPER_ARCH as cell, i (cell.fdi)}
                    {#if i === 8}<div class="mid-gap"></div>{/if}
                    <div class="face-cell" class:mirrored={cell.mirrored}>
                        <svg viewBox={ODONTO_PATHS[cell.ref.face].viewBox} aria-label="Face oclusal do dente {cell.fdi}">
                            {#each ODONTO_PATHS[cell.ref.face].parts ?? [] as part (part.label)}
                                {@const surface = surfaceFor(part.label, 'upper')}
                                {@const regionKey = `${cell.fdi}:${surface}`}
                                <path
                                    class="face-region"
                                    d={part.d}
                                    transform={RAW_TRANSFORM}
                                    style:fill={stages.find((s) => s.label === value[regionKey])?.color ?? null}
                                    class:readonly={readOnly}
                                    role="button"
                                    tabindex={readOnly ? -1 : 0}
                                    aria-label="Dente {cell.fdi}, face {surface}"
                                    on:click={() => onFacePartClick(cell.fdi, surface)}
                                    on:keydown={(e) => onFacePartKeydown(e, cell.fdi, surface)}
                                />
                            {/each}
                            <!-- Arte de linha (moldura + divisões) por cima das regiões — só fill,
                                 sem stroke (mesmo motivo do dente: stroke duplicaria o contorno). -->
                            <path class="line-art" d={ODONTO_PATHS[cell.ref.face].d} transform={RAW_TRANSFORM} />
                        </svg>
                    </div>
                {/each}
            </div>

            <div class="arch-row numbers">
                {#each UPPER_ARCH as cell, i (cell.fdi)}
                    {#if i === 8}<div class="mid-gap"></div>{/if}
                    <span class="fdi-num">{cell.fdi}</span>
                {/each}
            </div>

            <div class="arch-sep"></div>

            <div class="arch-row numbers">
                {#each LOWER_ARCH as cell, i (cell.fdi)}
                    {#if i === 8}<div class="mid-gap"></div>{/if}
                    <span class="fdi-num">{cell.fdi}</span>
                {/each}
            </div>

            <div class="arch-row">
                {#each LOWER_ARCH as cell, i (cell.fdi)}
                    {#if i === 8}<div class="mid-gap"></div>{/if}
                    <div class="face-cell" class:mirrored={cell.mirrored}>
                        <svg viewBox={ODONTO_PATHS[cell.ref.face].viewBox} aria-label="Face oclusal do dente {cell.fdi}">
                            {#each ODONTO_PATHS[cell.ref.face].parts ?? [] as part (part.label)}
                                {@const surface = surfaceFor(part.label, 'lower')}
                                {@const regionKey = `${cell.fdi}:${surface}`}
                                <path
                                    class="face-region"
                                    d={part.d}
                                    transform={RAW_TRANSFORM}
                                    style:fill={stages.find((s) => s.label === value[regionKey])?.color ?? null}
                                    class:readonly={readOnly}
                                    role="button"
                                    tabindex={readOnly ? -1 : 0}
                                    aria-label="Dente {cell.fdi}, face {surface}"
                                    on:click={() => onFacePartClick(cell.fdi, surface)}
                                    on:keydown={(e) => onFacePartKeydown(e, cell.fdi, surface)}
                                />
                            {/each}
                            <!-- Arte de linha (moldura + divisões) por cima das regiões — só fill,
                                 sem stroke (mesmo motivo do dente: stroke duplicaria o contorno). -->
                            <path class="line-art" d={ODONTO_PATHS[cell.ref.face].d} transform={RAW_TRANSFORM} />
                        </svg>
                    </div>
                {/each}
            </div>

            <div class="tooth-band tooth-band-lower">
                <div class="arch-row">
                    {#each LOWER_ARCH as cell, i (cell.fdi)}
                        {#if i === 8}<div class="mid-gap"></div>{/if}
                        {@const toothColor = toothDisplayColor(value, stages, cell.fdi)}
                        <button type="button" class="tooth-cell" class:mirrored={cell.mirrored} disabled={readOnly} on:click={() => onToothClick(cell.fdi)}>
                            <svg viewBox={ODONTO_PATHS[cell.ref.tooth].viewBox} aria-label="Dente {cell.fdi}">
                                <!-- Conteúdo (cor do estágio) por baixo; a arte de linha (sempre escura) por
                                     cima — ver comentário em odontograma-paths.generated.ts sobre por que a
                                     arte de linha é só fill (sem stroke): stroke duplicava o contorno. -->
                                <path class="tooth-content" d={ODONTO_PATHS[cell.ref.tooth].background} transform={RAW_TRANSFORM} style:fill={toothColor} />
                                <path class="line-art" d={ODONTO_PATHS[cell.ref.tooth].d} transform={RAW_TRANSFORM} />
                                {#if cell.ref.sliver != null}
                                    <path class="line-art" d={ODONTO_PATHS[cell.ref.sliver].d} transform={RAW_TRANSFORM} />
                                {/if}
                            </svg>
                        </button>
                    {/each}
                </div>
                <div class="gum-band"></div>
            </div>
        </div>
    {/if}

    {#if showDeciduos}
        {#if plain}<h4 class="denticao-title">Dentes Decíduos</h4>{/if}
        <div class="odonto-body decid-body">
            <div class="tooth-band tooth-band-upper">
                <div class="gum-band"></div>
                <div class="arch-row decid">
                    {#each UPPER_ARCH_DECID as cell, i (cell.fdi)}
                        {#if i === 5}<div class="mid-gap"></div>{/if}
                        {@const toothColor = toothDisplayColor(value, stages, cell.fdi)}
                        <button type="button" class="tooth-cell" class:mirrored={cell.mirrored} disabled={readOnly} on:click={() => onToothClick(cell.fdi)}>
                            <svg viewBox={ODONTO_PATHS[cell.ref.tooth].viewBox} aria-label="Dente {cell.fdi}">
                                <path class="tooth-content" d={ODONTO_PATHS[cell.ref.tooth].background} transform={RAW_TRANSFORM} style:fill={toothColor} />
                                <path class="line-art" d={ODONTO_PATHS[cell.ref.tooth].d} transform={RAW_TRANSFORM} />
                                {#if cell.ref.sliver != null}
                                    <path class="line-art" d={ODONTO_PATHS[cell.ref.sliver].d} transform={RAW_TRANSFORM} />
                                {/if}
                            </svg>
                        </button>
                    {/each}
                </div>
            </div>

            <div class="arch-row decid">
                {#each UPPER_ARCH_DECID as cell, i (cell.fdi)}
                    {#if i === 5}<div class="mid-gap"></div>{/if}
                    <div class="face-cell" class:mirrored={cell.mirrored}>
                        <svg viewBox={ODONTO_PATHS[cell.ref.face].viewBox} aria-label="Face oclusal do dente {cell.fdi}">
                            {#each ODONTO_PATHS[cell.ref.face].parts ?? [] as part (part.label)}
                                {@const surface = surfaceFor(part.label, 'upper')}
                                {@const regionKey = `${cell.fdi}:${surface}`}
                                <path
                                    class="face-region"
                                    d={part.d}
                                    transform={RAW_TRANSFORM}
                                    style:fill={stages.find((s) => s.label === value[regionKey])?.color ?? null}
                                    class:readonly={readOnly}
                                    role="button"
                                    tabindex={readOnly ? -1 : 0}
                                    aria-label="Dente {cell.fdi}, face {surface}"
                                    on:click={() => onFacePartClick(cell.fdi, surface)}
                                    on:keydown={(e) => onFacePartKeydown(e, cell.fdi, surface)}
                                />
                            {/each}
                            <path class="line-art" d={ODONTO_PATHS[cell.ref.face].d} transform={RAW_TRANSFORM} />
                        </svg>
                    </div>
                {/each}
            </div>

            <div class="arch-row decid numbers">
                {#each UPPER_ARCH_DECID as cell, i (cell.fdi)}
                    {#if i === 5}<div class="mid-gap"></div>{/if}
                    <span class="fdi-num">{cell.fdi}</span>
                {/each}
            </div>

            <div class="arch-sep"></div>

            <div class="arch-row decid numbers">
                {#each LOWER_ARCH_DECID as cell, i (cell.fdi)}
                    {#if i === 5}<div class="mid-gap"></div>{/if}
                    <span class="fdi-num">{cell.fdi}</span>
                {/each}
            </div>

            <div class="arch-row decid">
                {#each LOWER_ARCH_DECID as cell, i (cell.fdi)}
                    {#if i === 5}<div class="mid-gap"></div>{/if}
                    <div class="face-cell" class:mirrored={cell.mirrored}>
                        <svg viewBox={ODONTO_PATHS[cell.ref.face].viewBox} aria-label="Face oclusal do dente {cell.fdi}">
                            {#each ODONTO_PATHS[cell.ref.face].parts ?? [] as part (part.label)}
                                {@const surface = surfaceFor(part.label, 'lower')}
                                {@const regionKey = `${cell.fdi}:${surface}`}
                                <path
                                    class="face-region"
                                    d={part.d}
                                    transform={RAW_TRANSFORM}
                                    style:fill={stages.find((s) => s.label === value[regionKey])?.color ?? null}
                                    class:readonly={readOnly}
                                    role="button"
                                    tabindex={readOnly ? -1 : 0}
                                    aria-label="Dente {cell.fdi}, face {surface}"
                                    on:click={() => onFacePartClick(cell.fdi, surface)}
                                    on:keydown={(e) => onFacePartKeydown(e, cell.fdi, surface)}
                                />
                            {/each}
                            <path class="line-art" d={ODONTO_PATHS[cell.ref.face].d} transform={RAW_TRANSFORM} />
                        </svg>
                    </div>
                {/each}
            </div>

            <div class="tooth-band tooth-band-lower">
                <div class="arch-row decid">
                    {#each LOWER_ARCH_DECID as cell, i (cell.fdi)}
                        {#if i === 5}<div class="mid-gap"></div>{/if}
                        {@const toothColor = toothDisplayColor(value, stages, cell.fdi)}
                        <button type="button" class="tooth-cell" class:mirrored={cell.mirrored} disabled={readOnly} on:click={() => onToothClick(cell.fdi)}>
                            <svg viewBox={ODONTO_PATHS[cell.ref.tooth].viewBox} aria-label="Dente {cell.fdi}">
                                <path class="tooth-content" d={ODONTO_PATHS[cell.ref.tooth].background} transform={RAW_TRANSFORM} style:fill={toothColor} />
                                <path class="line-art" d={ODONTO_PATHS[cell.ref.tooth].d} transform={RAW_TRANSFORM} />
                                {#if cell.ref.sliver != null}
                                    <path class="line-art" d={ODONTO_PATHS[cell.ref.sliver].d} transform={RAW_TRANSFORM} />
                                {/if}
                            </svg>
                        </button>
                    {/each}
                </div>
                <div class="gum-band"></div>
            </div>
        </div>
    {/if}

    {#if stages.length > 0}
        <!-- Só os estágios com nome entram na legenda — o indefinido (branco) não precisa. -->
        <div class="legend">
            {#each stages as stage (stage.label)}
                <span class="legend-item">
                    <span class="legend-swatch" style:border-color={stage.color}></span>
                    {stage.label}
                </span>
            {/each}
        </div>
    {/if}

    {#if plain}
        {@const notes = teethWithNotes(value)}
        {#if notes.length > 0}
            <!-- O odontograma de input (onde a anotação é editada) não aparece na impressão — as
                 anotações de todos os dentes marcados aparecem aqui, uma por linha. -->
            <div class="notes-print">
                {#each notes as entry (entry.fdi)}
                    <p class="notes-print-item"><strong>Dente {entry.fdi}:</strong> {entry.note}</p>
                {/each}
            </div>
        {/if}
    {/if}
</div>

<style>
    .odonto {
        display: flex;
        flex-direction: column;
        gap: 16px;
        width: 100%;
    }
    .odonto-header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
    }
    /* Só aparece na impressão (`plain`) — na tela o seletor Permanentes/Decíduos já deixa claro
       qual arco está sendo exibido, mas na impressão os dois podem aparecer empilhados (ver
       showDeciduos) e precisam de um título cada um. */
    .denticao-title {
        margin: 0;
        font-size: 0.9rem;
        font-weight: 700;
        color: #212529;
    }
    .seg {
        display: inline-flex;
        border: 1px solid #dee2e6;
        border-radius: 10px;
        overflow: hidden;
    }
    .seg button {
        border: none;
        background: #fff;
        color: #495057;
        font-size: 0.85rem;
        font-weight: 600;
        padding: 8px 18px;
        cursor: pointer;
    }
    .seg button.on {
        background: var(--cs2-accent, #1bdf6f);
        color: #fff;
    }

    .odonto-body {
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    /* A faixa de gengiva fica atrás dos dentes, sobrepondo a metade da célula onde ficam as
       raízes (topo na arcada superior, base na inferior) — não é uma barra decorativa separada.
       `.tooth-band` tem só a altura da própria fileira de dentes (a gengiva sai do fluxo via
       `position: absolute`); `.arch-row` ganha `z-index` maior pra ser desenhada por cima. */
    .tooth-band {
        position: relative;
    }
    .tooth-band .arch-row {
        position: relative;
        z-index: 1;
    }
    .gum-band {
        position: absolute;
        left: 0;
        right: 0;
        height: 56px;
        border-radius: 12px;
        background: #f7e3e3;
        z-index: 0;
        display: none;
    }
    .tooth-band-upper .gum-band {
        top: 0;
    }
    .tooth-band-lower .gum-band {
        bottom: 0;
    }
    .arch-sep {
        height: 1px;
        background: #dee2e6;
        margin: 6px 0;
    }
    .arch-row {
        display: grid;
        grid-template-columns: repeat(8, minmax(40px, 1fr)) 18px repeat(8, minmax(40px, 1fr));
        align-items: center;
        column-gap: 4px;
    }
    /* Decíduos: só 5 posições por quadrante (sem pré-molares nem 3º molar) — largura fixa (não
       "1fr") pra não esticar pelo container inteiro, e centralizado. */
    .arch-row.decid {
        grid-template-columns: repeat(5, minmax(40px, 64px)) 18px repeat(5, minmax(40px, 64px));
        justify-content: center;
    }
    @media print {
        /* No PDF/impressão (ctrl+p), `.print-page` perde o `max-width` (vira a largura cheia da
           página física) e a fileira decíduos some esticada de ponta a ponta — o `justify-content:
           center` do grid acima não é suficiente nesse pipeline de impressão. Restringir a largura
           aqui, no wrapper que já engloba as 4 fileiras (dentes/faces/números) de uma vez, resolve
           pros dois arcos junto. Valor calibrado visualmente contra a impressão real. */
        .odonto-body {
            border: 1px solid #ced4da;
            border-radius: 12px;
            padding: 12px 8px;
        }
        .odonto-body.decid-body {
            padding: 12px 20%;
        }
        /* Os permanentes usam a largura cheia (colunas "1fr") — numa página impressa mais estreita
           que a tela, o `column-gap` sozinho já é o suficiente pra estourar a largura disponível.
           As células já têm padding/margem própria (ver `.tooth-cell`), então zerar o gap entre
           colunas libera espaço sem apertar visualmente os dentes. */
        .arch-row {
            column-gap: 0;
        }
    }
    .mid-gap {
        width: 100%;
    }

    .tooth-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        border: none;
        outline: none;
        background: transparent;
        padding: 2px;
        cursor: pointer;
        border-radius: 6px;
    }
    .tooth-cell:hover {
        background: #f1f3f5;
    }
    .tooth-cell:disabled {
        cursor: not-allowed;
    }
    .tooth-cell:disabled:hover {
        background: transparent;
    }
    .tooth-cell:focus,
    .tooth-cell:focus-visible {
        outline: none;
    }
    .face-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 2px;
    }
    .tooth-cell svg {
        width: 100%;
        height: 88px;
        overflow: visible;
    }
    .face-cell svg {
        width: 100%;
        height: 44px;
        overflow: visible;
    }
    .tooth-cell.mirrored svg,
    .face-cell.mirrored svg {
        transform: scaleX(-1);
    }
    /* Conteúdo (por baixo) — branco por padrão (indefinido) ou a cor do estágio atual. */
    .tooth-cell :global(.tooth-content) {
        fill: #ffffff;
        stroke: none;
    }
    /* Arte de linha (por cima) — só fill, sem stroke: o desenho original já é "oco" (as subpaths
     * se cancelam pelo winding), aplicar stroke por cima duplica o contorno. Nunca capta clique
     * nem muda de cor — é sempre a moldura/divisões em escuro. */
    .odonto :global(.line-art) {
        fill: #343a40;
        stroke: none;
        pointer-events: none;
    }
    /* Recortes do próprio desenho (topo/esquerda/direita/centro/base) — branco por padrão
     * (indefinido) ou a cor do estágio atual; cada um clicável de forma independente. O contorno
     * (moldura + divisões) vem só da .line-art por cima, não daqui. */
    .face-cell :global(.face-region) {
        fill: #ffffff;
        stroke: none;
        cursor: pointer;
        outline: none;
    }
    .face-cell :global(.face-region:hover),
    .face-cell :global(.face-region:focus-visible) {
        fill: rgba(27, 223, 111, 0.28);
    }
    .face-cell :global(.face-region:focus),
    .face-cell :global(.face-region:focus-visible) {
        outline: none;
    }
    /* pointer-events:none também impede o :hover de disparar (sem precisar brigar com a regra
       de destaque acima na especificidade CSS). */
    .face-cell :global(.face-region.readonly) {
        cursor: not-allowed;
        pointer-events: none;
    }

    .fdi-num {
        text-align: center;
        font-size: 0.78rem;
        font-weight: 600;
        font-variant-numeric: tabular-nums;
        color: #495057;
    }

    .legend {
        display: flex;
        flex-wrap: wrap;
        gap: 16px;
        padding-top: 12px;
        border-top: 1px solid #dee2e6;
    }
    .legend-item {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        font-size: 0.85rem;
        color: #495057;
    }
    /* Cor vem 100% do `border` (não de `background`/`box-shadow`) de propósito: o Chrome só
       imprime `background`/`box-shadow` se "Gráficos em segundo plano" estiver marcado no diálogo
       de impressão (desmarcado por padrão) — aí a legenda toda vira quadrados brancos. `border`
       sempre imprime independente dessa opção, então uma caixa 0x0 com borda grossa (as 4 bordas
       se encontram no meio, preenchendo o quadrado inteiro) reproduz o mesmo efeito visual sem
       depender dela. */
    .legend-swatch {
        width: 0;
        height: 0;
        border: 7px solid transparent;
        flex-shrink: 0;
    }

    .notes-print {
        display: flex;
        flex-direction: column;
        gap: 4px;
        padding-top: 12px;
        border-top: 1px solid #dee2e6;
    }
    .notes-print-item {
        margin: 0;
        font-size: 0.75rem;
        color: #343a40;
    }
</style>
